import { type ExternalUserServiceIntegration } from '../../../../models/User'
import { type UserIntegrationIssue } from '../../../../store/user/user'
import { IntegrationServiceId } from '../../../../common/integrations'
import { CalendarProvider, MeetingPlatformProvider, type UserIntegrations } from '../../../../models/Scheduling'

export const INTEGRATION_SETTINGS_PATH = '/account/integration-settings/integrations'
/** The Scheduling profile settings' Integrations section: one card per supported calendar, each with its own connect action. */
export const SCHEDULING_INTEGRATIONS_PATH = '/account/profile-settings/scheduling?section=integrations'
export const integrationConnectPath = (serviceId: IntegrationServiceId): string => `${INTEGRATION_SETTINGS_PATH}/${serviceId}/settings?connect`
export const calendarSyncSettingsPath = (serviceId: IntegrationServiceId): string => `${INTEGRATION_SETTINGS_PATH}/${serviceId}/settings?section=calendarSync`

export interface CalendarSyncCopy {
  title: string
  subtitle: string
  tooltip: string
}

interface CalendarOptionBase {
  serviceId: IntegrationServiceId
  provider: CalendarProvider
  label: string
}

export type CalendarOption =
  | (CalendarOptionBase & { calendarSyncGated?: false; calendarSync?: undefined })
  | (CalendarOptionBase & {
      calendarSyncGated: true
      calendarSync: CalendarSyncCopy
    })

export const GOOGLE_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.GOOGLE_CALENDAR,
  provider: CalendarProvider.GOOGLE,
  label: 'Google Calendar',
}

export const OUTLOOK_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.OUTLOOK_CALENDAR,
  provider: CalendarProvider.OUTLOOK,
  label: 'Outlook Calendar',
}

export const EXCHANGE_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.EXCHANGE_CALENDAR,
  provider: CalendarProvider.EXCHANGE,
  label: 'Exchange Calendar (On-Premise)',
}

export const REDTAIL_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.REDTAIL,
  provider: CalendarProvider.REDTAIL,
  label: 'Redtail',
  calendarSyncGated: true,
  calendarSync: {
    title: 'Turn on Redtail calendar sync',
    subtitle: 'Scheduling uses your Redtail activities for availability and books meetings as Redtail activities. Turn on calendar sync to start scheduling.',
    tooltip: 'Scheduling needs Redtail calendar sync on to read your activities and book into Redtail.',
  },
}

export const CALENDAR_OPTIONS: Array<CalendarOption> = [GOOGLE_CALENDAR_OPTION, OUTLOOK_CALENDAR_OPTION, EXCHANGE_CALENDAR_OPTION, REDTAIL_CALENDAR_OPTION]

export const calendarOptionFor = (provider: CalendarProvider): CalendarOption | undefined => CALENDAR_OPTIONS.find((option) => option.provider === provider)

export enum IntegrationConnectionStatus {
  READY = 'ready',
  NEEDS_RECONNECT = 'needs_reconnect',
  NEEDS_CALENDAR_SYNC = 'needs_calendar_sync',
  NOT_CONNECTED = 'not_connected',
}

export interface IntegrationConnection {
  status: IntegrationConnectionStatus
  email?: string
}

export const getIntegrationConnection = (
  integrations: Array<ExternalUserServiceIntegration> | undefined,
  problems: Array<UserIntegrationIssue> | undefined,
  serviceId: IntegrationServiceId,
  /**
   * Determined by scheduling-be whether the user-integration has the required
   * scopes and permissions needed for scheduling to function properly
   */
  capable?: boolean,
  options?: { calendarSyncGated?: boolean },
): IntegrationConnection => {
  const integration = (integrations ?? []).find((candidate) => candidate.serviceId === serviceId)
  if (!integration) {
    return { status: IntegrationConnectionStatus.NOT_CONNECTED }
  }
  const hasProblem = (problems ?? []).some((problem) => problem.integrationId === integration.id)
  const email = integration.email ?? (integration.type === 'calendar' ? integration.externalAccountId : undefined)
  const isLiveWithNoProblem = integration.state === 'live' && !hasProblem

  if (isLiveWithNoProblem && capable !== false) {
    return { status: IntegrationConnectionStatus.READY, email }
  }
  if (isLiveWithNoProblem && capable === false && options?.calendarSyncGated) {
    return { status: IntegrationConnectionStatus.NEEDS_CALENDAR_SYNC, email }
  }
  return { status: IntegrationConnectionStatus.NEEDS_RECONNECT, email }
}

export const getSchedulingCapability = (integrations: UserIntegrations, provider: CalendarProvider | MeetingPlatformProvider): boolean | undefined =>
  [...integrations.calendars, ...integrations.meetingPlatforms].find((integration) => integration.provider === provider)?.capable

/**
 * Whether to offer the Microsoft tenant-wide admin consent link for the Outlook calendar.
 */
export const needsOutlookAdminApproval = (integrations: UserIntegrations): boolean => {
  const outlook = integrations.calendars.find((calendar) => calendar.provider === CalendarProvider.OUTLOOK)
  return outlook != null && outlook.connected && !outlook.capable
}

export const NATIVE_PLATFORM_BY_CALENDAR: Partial<Record<CalendarProvider, MeetingPlatformProvider>> = {
  [CalendarProvider.GOOGLE]: MeetingPlatformProvider.GOOGLE_MEET,
  [CalendarProvider.OUTLOOK]: MeetingPlatformProvider.MS_TEAMS,
}

export const isPlatformCompatibleWithCalendar = (platform: MeetingPlatformProvider, calendar: CalendarProvider): boolean =>
  platform === MeetingPlatformProvider.ZOOM || NATIVE_PLATFORM_BY_CALENDAR[calendar] === platform

export const resolveIntegrationsOnCalendarSelect = (current: UserIntegrations, calendar: CalendarProvider, isZoomSelectable: boolean): UserIntegrations => {
  const keepZoom = current.selectedMeetingPlatform === MeetingPlatformProvider.ZOOM && isZoomSelectable
  return {
    ...current,
    selectedCalendar: calendar,
    // A calendar with no native platform (Exchange, Redtail) can only pair with Zoom, so we fall
    // back to Zoom when it's selectable rather than leaving a stale platform from the old calendar.
    selectedMeetingPlatform: keepZoom
      ? MeetingPlatformProvider.ZOOM
      : (NATIVE_PLATFORM_BY_CALENDAR[calendar] ?? (isZoomSelectable ? MeetingPlatformProvider.ZOOM : null)),
  }
}
